import { RunContextApp } from "framework/RCApp";
import { ErrorUtils } from "framework/utility/ErrorUtils";
import { AxiosInstance } from "axios";
import { SaveProjectConfigData } from "types/apis/pilotInterfaces";
import axios from "axios";


export interface BffApiResponse<T> {
  data?: T;
  error?: any;
}

/**
 * URL prefix for proxypulse's insight blueprint (DA-2385 / DA-2401, epic DA-697).
 *
 * Paths below were **verified against proxypulse `routes/v1/insights.py` on
 * 2026-09-11**. Four of them were wrong when first written from the ticket
 * description alone (`/update-all`, `/publish-all`, `/unpublish-all`,
 * `/discard-drafts` — the real routes drop the `-all`/`-drafts` suffix), so
 * check the source rather than a ticket when a route changes.
 */
const BFF_INSIGHT_BASE = 'v1/insights';

export class BffApiClient {
  private client !: any
  private axiosClient: AxiosInstance

  constructor(private rc: RunContextApp, private toolUrl: string) {
    this.axiosClient = axios.create()
    this.init()
  }

  init() {
    // Set up request interceptor to automatically add authorization header
    this.axiosClient.interceptors.request.use(request => {
      const authToken = this.rc.session.getCMSAccessToken();
      if (authToken) {
        request.headers.Authorization = `Bearer ${authToken}`;
      }
      return request;
    });

    // Set up response interceptor for error handling
    this.axiosClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  initWithJwt(jwt: string) {
    this.axiosClient.defaults.headers.common['Authorization'] = `Bearer ${jwt}`
  }

  async login(id: string, password: string){
    try {
      const loginResp = await this.axiosClient.post(`${this.toolUrl}/v1/login`, {
        email: id,
        password: password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return loginResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/login\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getProjects() {
    try {
      const projectsResp = await this.axiosClient.get(`${this.toolUrl}/v1/projects`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return projectsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getStrapiProjects() {
    try {
      const strapiProjectsResp = await this.axiosClient.get(`${this.toolUrl}/v1/strapi-projects`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return strapiProjectsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/strapi-projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getCmsPilots(pilotId: string) {
    try {
      const cmsPilotsResp = await this.axiosClient.get(`${this.toolUrl}/v1/cms-pilots`, {
        params: { pilot_id: pilotId },
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return cmsPilotsResp;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : ${this.toolUrl}/v1/cms-pilots\n` +
        `\nresulted in error : \n`, error);

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      };
    }
  }

  async getUtilities() {
    try {
      const utilitiesResp = await this.axiosClient.get(`${this.toolUrl}/v1/utilities`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return utilitiesResp

    } catch (error) { 
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/utilities\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createUtility(utilityData: any) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/utilities`, utilityData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/utilities\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createEnvironment(environmentData: {
    // New API shape (preferred)
    name: string;
    awsRegion?: string;
    serverUrl?: string;
    type?: string;
    frontend_url?: string;
    utility_id?: number;
    utilityName?: string;
    stackName?: string;
    stateAlphaCode?: string;

    // Aliases/back-compat (harmless if backend ignores)
    environment_name?: string;
    aws_region?: string;
    data_server_url?: string;
    [key: string]: any;
  }) {
    
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/environments`, environmentData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async listEnvironments(params?: { id?: number | string }) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/environments`, {
        params,
        headers: { 'Content-Type': 'application/json' },
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/environments\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async listEnvironmentsByUtility(body: { utility_id: number }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/environments/by-utility`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/environments/by-utility\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getAwsRegions() {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/environments/aws-regions`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/environments/aws-regions\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getEnvironments(utilityId: number) {
    try {
      // Backward-compat: prefer server-side filter if available
      const resp: any = await this.listEnvironmentsByUtility({ utility_id: utilityId });
      return resp;

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/environments/by-utility\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getAllEnvironments() {
    try {
      // DB-only list (no external call)
      const allEnvironmentsResp = await this.axiosClient.get(`${this.toolUrl}/v1/all-environments`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return allEnvironmentsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/all-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async updateEnvironment(
    environmentId: number,
    body: {
      environment_name: string;
      data_server_url: string;
      frontend_url: string;
      aws_region?: string;
      // Allow backend-compatible alias keys if needed
      [key: string]: any;
    }
  ) {
    try {
      const updateResp = await this.axiosClient.put(
        `${this.toolUrl}/v1/environment/${environmentId}`,
        body,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return updateResp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PUT request to : /v1/environment/${environmentId}\n` +
          `\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getAssociatedEnvironments(projectId: string) {
    try {
      const associatedEnvironmentsResp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/associated-environments`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return associatedEnvironmentsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/projects/${projectId}/associated-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async associateEnvironment(projectId: string, environmentId: string) {
    try {
      const associateEnvironmentResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects/${projectId}/associated-environments`, {
        environment_id: environmentId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return associateEnvironmentResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects/${projectId}/associated-environments\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }


  async checkProcessStatus(projectId: string,processId: string) {
    try {
      
      const statusResp = await this.axiosClient.get(`${this.toolUrl}/v1/check-status/${projectId}/${processId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return statusResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/check-status/<process_id>` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async checkProjectStatus(projectId: string) {
    try {
      const statusResp = await this.axiosClient.get(`${this.toolUrl}/v1/check-project-status/${projectId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return statusResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/check-project-status/<project_id>` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createProject(projectData: any) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects`, projectData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async validateProject(validationData: {
    environment: string;
    queue_suffix: string;
    utility_name: string;
    pilot_id: string;
    state_alpha_code: string;
    customer_type: string;
  }) {
    try {
      const validateResp = await this.axiosClient.post(`${this.toolUrl}/v1/project/validate`, validationData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return validateResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/project/validate\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createProjectAndPushConfigs(projectId: string) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/project-management/create-project-and-push-configs`, {
        project_id: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/project-management/create-project-and-push-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createRecoProject(projectId: string, sourcePilotId: string) {
    try {
      const createResp = await this.axiosClient.post(`${this.toolUrl}/v1/projects/reco`, {
        projectId,
        sourcePilotId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/projects/reco\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async updateProject(projectId: string, projectData: any) {
    try {
      // Include projectId in the payload instead of URL
      const payloadWithProjectId = {
        ...projectData,
        projectId: projectId
      };
      
      const updateResp = await this.axiosClient.patch(`${this.toolUrl}/v1/projects`, payloadWithProjectId, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return updateResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> PATCH request to : /v1/projects\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getAnswer(requestData: { question: string; user_id: string; context_id?: string }) {
    try {
      const answerResp = await this.axiosClient.post(`${this.toolUrl}/chat`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return answerResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /answer\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPilotConfigs(pilotId: string){
    try {
      const pilotConfigsResp = await this.axiosClient.get(`${this.toolUrl}/v1/pilot-configs`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          pilotId: pilotId
        }
      })

      return pilotConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/pilot-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPilotConfigsWithFilters(projectId: string, apiHost: string, application?: string, fuelType?: string) {
    try {
      const requestData: any = {
        project_id: projectId,
        api_host: apiHost
      };

      if (application && application !== 'ALL') {
        requestData.module_name = application;
      }

      if (fuelType && fuelType !== 'All') {
        requestData.fuel_type = fuelType;
      }

      const pilotConfigsResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-pilot-configs`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return pilotConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/get-pilot-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getIngestionConfigs(pilotId: string, params?: { level?: string; levels?: string[]; productType?: string; fuelType?: string; cluster_reference?: string; cluster_id?: string }){
    try {
      let url = `${this.toolUrl}/v1/configs/${pilotId}`;
      if (params && Object.keys(params).length > 0) {
        const search = new URLSearchParams();
        if (params.levels != null && params.levels.length > 0) {
          params.levels.forEach((l) => search.append('level', l));
        } else if (params.level != null && params.level !== '') {
          search.set('level', params.level);
        }
        if (params.productType != null && params.productType !== '') search.set('productType', params.productType);
        if (params.fuelType != null && params.fuelType !== '') search.set('fuelType', params.fuelType);
        if (params.cluster_reference != null && params.cluster_reference !== '') search.set('cluster_reference', params.cluster_reference);
        if (params.cluster_id != null && params.cluster_id !== '') search.set('cluster_id', params.cluster_id);
        const qs = search.toString();
        if (qs) url += `?${qs}`;
      }
      const ingestionConfigsResp = await this.axiosClient.get(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return ingestionConfigsResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/configs/${pilotId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getConfigOverrides(pilotId: string, params?: { config_key?: string; config_type?: string }) {
    try {
      let url = `${this.toolUrl}/v1/configs/${pilotId}/overrides`;
      if (params && Object.keys(params).length > 0) {
        const search = new URLSearchParams();
        if (params.config_key != null && params.config_key !== '') search.set('config_key', params.config_key);
        if (params.config_type != null && params.config_type !== '') search.set('config_type', params.config_type);
        const qs = search.toString();
        if (qs) url += `?${qs}`;
      }
      const overridesResp = await this.axiosClient.get(url, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return overridesResp;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/configs/${pilotId}/overrides\n` +
        `\nresulted in error : \n`, error);

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      };
    }
  }

  async saveProjectConfigs(configsData: SaveProjectConfigData[]){
    try {
      const saveConfigResp = await this.axiosClient.post(`${this.toolUrl}/v1/save_project_config`, configsData, {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      return saveConfigResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/save_project_config\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // Keep the old method for backward compatibility
  async saveProjectConfig(configData: SaveProjectConfigData){
    return this.saveProjectConfigs([configData]);
  }

  async compareConfigTools(body: { projectId: string; sourceEnvId: number; targetEnvId: number }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/config-tools/compare`, body, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/config-tools/compare\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Alias for Config Tools Suite naming
  async compareConfigToolsSuite(payload: { projectId: string; sourceEnvId: number; targetEnvId: number; locale?: string }) {
    return this.compareConfigTools(payload);
  }

  async pushConfigToolsSync(body: { projectId: string; sourceEnvId: number; targetEnvId: number; items: any[] }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/config-tools/sync/push`, body, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/config-tools/sync/push\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Config Tools Suite naming
  async pushPromotion(payload: { projectId: string; sourceEnvId: number; targetEnvId: number; items: any[] }) {
    return this.pushConfigToolsSync(payload);
  }

  async getConfigToolsSyncStatus(syncId: string) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/config-tools/sync/status/${syncId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/config-tools/sync/status/${syncId}\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Config Tools Suite naming
  async getPromotionStatus(syncId: string) {
    return this.getConfigToolsSyncStatus(syncId);
  }

  /** Request body: single object { projectId, sourceEnvId, targetEnvironments, items } for POST /v1/config-tools/sync/create-shell-bundle */
  async createConfigToolsShellBundle(body: { projectId: string; sourceEnvId: number; targetEnvironments: Array<{ id: number; name: string }>; items: any[] }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/config-tools/sync/create-shell-bundle`, body, {
        headers: {
          'Content-Type': 'application/json'
        },
        responseType: 'blob' // Always expect ZIP file as binary data
      });
      // Return full response including headers for Content-Disposition extraction
      return {
        data: resp.data, // Blob data
        headers: resp.headers, // Response headers
        status: resp.status,
        statusText: resp.statusText
      };
    } catch (error: any) {
      // Check if error response contains blob data (some servers return error as blob)
      if (error.response && error.response.data instanceof Blob) {
        // If error response is a blob, it might actually be the ZIP file
        return {
          data: error.response.data,
          headers: error.response.headers || {},
          status: error.response.status,
          statusText: error.response.statusText
        };
      }
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/config-tools/sync/create-shell-bundle\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Config Tools Suite naming
  async createShellBundle(payload: { projectId: string; sourceEnvId: number; targetEnvironments: Array<{ id: number; name: string }>; items: any[] }) {
    return this.createConfigToolsShellBundle(payload);
  }

  /**
   * Download an existing shell bundle by ID (same API as Create PR download).
   * GET /v1/config-tools/sync/shell-bundle/:shellBundleId/download
   * Returns blob + headers (e.g. Content-Disposition) for programmatic download.
   * 
   * Or download with payload (POST) to regenerate bundle from status data.
   * POST /v1/config-tools/sync/shell-bundle/download
   */
  async downloadShellBundle(shellBundleId: string | number | { projectId: string; sourceEnvId: number; targetEnvironments: Array<{ id: number; name: string }>; items: any[] }) {
    try {
      // If payload object is provided, use POST with payload
      if (typeof shellBundleId === 'object' && shellBundleId !== null && 'projectId' in shellBundleId) {
        const resp = await this.axiosClient.post(
          `${this.toolUrl}/v1/config-tools/sync/shell-bundle/download`,
          shellBundleId,
          {
            responseType: 'blob',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/zip'
            }
          }
        );
        const status = resp.status ?? 0;
        if (status >= 200 && status < 300) {
          return {
            data: resp.data,
            headers: resp.headers,
            status: resp.status,
            statusText: resp.statusText
          };
        }
        this.rc.error(
          this.rc.getName(this),
          `Error >> POST request to : /v1/config-tools/sync/shell-bundle/download\n\nresulted in error : \n`,
          { status, statusText: resp.statusText }
        );
        return { error: ErrorUtils.getApiErrorInfo({ response: resp }) };
      }
      
      // Otherwise, use GET with shellBundleId
      const resp = await this.axiosClient.get(
        `${this.toolUrl}/v1/config-tools/sync/shell-bundle/${shellBundleId}/download`,
        {
          responseType: 'blob',
          headers: { Accept: 'application/zip' }
        }
      );
      const status = resp.status ?? 0;
      if (status >= 200 && status < 300) {
        return {
          data: resp.data,
          headers: resp.headers,
          status: resp.status,
          statusText: resp.statusText
        };
      }
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/config-tools/sync/shell-bundle/${shellBundleId}/download\n\nresulted in error : \n`,
        { status, statusText: resp.statusText }
      );
      return { error: ErrorUtils.getApiErrorInfo({ response: resp }) };
    } catch (error: any) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> request to : /v1/config-tools/sync/shell-bundle/download\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async saveResolvedConfigs(body: { projectId: string; resolvedConfigs: Array<{ envId: number; configKey: string; configType: string; resolvedValue: string }> }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/config-tools/resolved-configs`, body, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/config-tools/resolved-configs\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getPromotionMatrix() {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/config-tools/promotion-matrix`, {
        headers: { 'Content-Type': 'application/json' }
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/config-tools/promotion-matrix\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getConfigToolsHistory(params: {
    projectId: string;
    envId?: number;
    targetEnvIds?: number[];
    page?: number;
    pageSize?: number;
    eventType?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    component?: string;
  }) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/config-tools/history`, {
        params,
        paramsSerializer: (p) => {
          const searchParams = new URLSearchParams();
          Object.keys(p).forEach((key) => {
            const value = (p as Record<string, unknown>)[key];
            if (value === undefined || value === null) return;
            if (Array.isArray(value)) {
              value.forEach((v) => searchParams.append(key, String(v)));
            } else {
              searchParams.append(key, String(value));
            }
          });
          return searchParams.toString();
        },
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/config-tools/history\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getConfigToolsHistoryEvent(eventId: string | number) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/config-tools/history/${eventId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });
      return resp;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/config-tools/history/${eventId}\n\nresulted in error : \n`,
        error
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getConfigRegistry(params?: { supported_meter_types?: string; allowed_measurement_type?: string; default_user_segment?: string }){
    try {
      const configRegistryResp = await this.axiosClient.get(`${this.toolUrl}/v1/get_config_registry`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params
      })

      return configRegistryResp
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/get_config_registry\n` +
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getStringRegistry(){
    try {
      const stringRegistryResp = await this.axiosClient.get(`${this.toolUrl}/v1/get_string_registry`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return stringRegistryResp
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/get_string_registry\n` +
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getApplicationsMetadata(api_host: string, pilot_id: string) {
    try {
      
      const applicationsMetadataResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-project-application-metadata`, {
        api_host: api_host,
        project_id: pilot_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return applicationsMetadataResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : ${this.toolUrl}/v1/get-project-application-metadata` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getProjectAttributes(project_id: string) {
    try {
      
      const projectAttributesResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-project-attributes`, {
        project_id: project_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return projectAttributesResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : ${this.toolUrl}/v1/get-project-attributes` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getClusterConfigs(cluster_id: string, projectId: string) {
    try {
      const clusterConfigResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-cluster-config`, {
        cluster_id: cluster_id,
        project_id: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return clusterConfigResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /get-cluster-config\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }




  async getClusters(project_id: string) {
    try {
      
      const clustersResp = await this.axiosClient.post(`${this.toolUrl}/v1/get-pilot-clusters`, {
        project_id: project_id
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return clustersResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : ${this.toolUrl}/v1/get-pilot-clusters\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getClustersList(projectId: string, cluster_categories?: string) {
    try {
      const clustersResp = await this.axiosClient.get(`${this.toolUrl}/v1/get-clusters/${projectId}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: cluster_categories ? { cluster_categories } : undefined
      })

      return clustersResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : ${this.toolUrl}/v1/get-clusters/${projectId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createConfigTypes(api_host: string, configData: any) {
    try {
      // Create a separate axios instance for this request to bypass automatic token handling
      const customAxiosClient = axios.create();
      
      const createConfigResp = await customAxiosClient.post(`https://${api_host}/config_types`, configData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return createConfigResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : https://${api_host}/config_types\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async predictConfigs(formData: any) {
    try {
      const predictConfigsResp = await this.axiosClient.post(`http://localhost:5000/predict-configs`, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return predictConfigsResp

    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : http://localhost:5000/predict-configs\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async pushConfigsToUat(projectId: string, sourceName?: string) {
    try {
      const pushConfigsToUatResp = await this.axiosClient.post(`${this.toolUrl}/v1/push-configs-to-dataserver`, {
        project_id: projectId,
        source_name:sourceName
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return pushConfigsToUatResp
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/push-configs-to-dataserver\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getPendingUatConfigs(projectId: string) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/pending-uat-configs`, {
        headers: {
          'Accept': 'application/json'
        }
      });
      return resp;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/projects/${projectId}/pending-uat-configs\n` +
        `\nresulted in error : \n`, error);
      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }
  
  // Data Generator API Methods
  async fetchUserDetails(requestData: {
    userMeterFuelMap: Record<string, string[]>;  
    utilityId?: number;
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    destinationPilotId?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/userDetails`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error)

      if (error?.response?.data?.errorDetails) {
        return { error: error.response.data.errorDetails };
      } else if (error?.response?.data?.message) {
        return { error: error.response.data.message };
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // New GET method for userDetails
  async getUserDetails(requestData: {
    userMeterMapping: Record<string, string[]>; 
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userMeterMapping: JSON.stringify(requestData.userMeterMapping),
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/userDetails?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/userDetails\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchRawData(requestData: {
    userMeterMapping: Record<string, string[]>;  
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userMeterMapping: JSON.stringify(requestData.userMeterMapping),
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/rawData?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/rawData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchBillingData(requestData: {
    userMeterMapping: Record<string, string[]>;  
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    hasBBUserProgram?: boolean;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userMeterMapping: JSON.stringify(requestData.userMeterMapping),
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        sourceUsername: requestData.sourceUsername,
        sourcePassword: requestData.sourcePassword
      });

      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      if (requestData.destinationEnv) {
        queryParams.append('destinationEnv', requestData.destinationEnv);
      }

      if (requestData.hasBBUserProgram !== undefined) {
        queryParams.append('hasBBUserProgram', requestData.hasBBUserProgram.toString());
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/billingData?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/billingData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchMeterData(requestData: {
    userUUID: string[];
    utilityId?: number; // Made optional since backend will assign it
    utilityName: string;
    sourceEnvironment: string;
    sourceUsername: string;
    sourcePassword: string;
    houseType?: string;
    subPilot?: string;
    ingestionBucket?: string;
    fileUploadBucket: string;
    destinationPilotId?: string;
    destinationEnv?: string;
    meterFuel?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/meterData`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/fetchProdUserData/meterData\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async saveUserMetadata(requestData: {
    first_name: string;
    last_name?: string;
    rate_plan: string;
    billing_cycle?: string;
    bb_duration?: string;
    primary_email: string;
    secondary_email?: string;
    address: string;
    meter_type?: string;
    dcx_clusters?: string;
    zipcode?: string;
    persona_name?: string;
    persona_description?: string;
    source_uuid: string;
    source_pilot_id?: string;
    destination_pilot_id?: string;
    source_env: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/user-metadata`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error)

      if (error?.response?.data?.message) {
        return {
          error: error.response.data.message
        }
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async deleteUserMetadata(recordId: number) {
    try {
      const response = await this.axiosClient.delete(`${this.toolUrl}/v1/user-metadata/${recordId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> DELETE request to : /v1/user-metadata/${recordId}\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async getUserMetadata(sourceUuid: string, sourceEnv: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/user-metadata`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          source_uuid: sourceUuid,
          source_env: sourceEnv
        }
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/user-metadata\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  // New method to fetch all user metadata without query parameters
  async getAllUserMetadata(destinationPilotId?: string, status?: string) {
    try {
      let url = `${this.toolUrl}/v1/user-metadata`;
      const params = new URLSearchParams();
      
      if (destinationPilotId) {
        params.append('destination_pilot_id', destinationPilotId);
      }
      
      if (status) {
        params.append('status', status);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await this.axiosClient.get(url);
      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/user-metadata\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  async getProjectLocales(projectId: string) {
    try {
      const resp = await this.axiosClient.get(`${this.toolUrl}/v1/projects/${projectId}/locales`, {
        headers: {
          'Content-Type': 'application/json'
        }
      })
      return resp
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/projects/${projectId}/locales\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  async fetchRatePlans(apiHost: string | undefined, utilityId: string) {
    try {
      const params: { api_host?: string; utility_id: string } = {
        utility_id: utilityId
      };
      
      if (apiHost) {
        params.api_host = apiHost;
      }
      
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-rates-plans`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params
      })

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetch-rates-plans\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async createBillingCycleSchedule(requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceUUID: string;
    sourceEnv: string;
    utilityName: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destinationPilotId: requestData.destinationPilotId,
        destinationEnv: requestData.destinationEnv,
        sourceUUID: requestData.sourceUUID,
        sourceEnv: requestData.sourceEnv,
        utilityName: requestData.utilityName
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/createBCSchedule?${queryParams.toString()}`);

      if (response.status === 207) {
        return response;
      }

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/fetchProdUserData/createBCSchedule\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async fetchUserUUID(requestData: {
    destinationPilotId: string;
    destinationEnv: string;
    sourceEnv: string;
    sourceUUID: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destinationPilotId: requestData.destinationPilotId,
        destinationEnv: requestData.destinationEnv,
        sourceEnv: requestData.sourceEnv,
        sourceUUID: requestData.sourceUUID
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/fetchUserUUID?${queryParams.toString()}`);

      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /fetchUserUUID\n` + 
        `\nresulted in error : \n`, error)

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async assignShcCluster(requestData: {
    uuid: string;
    data_server_url?: string;
    destination_pilot_id: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/assignShcCluster`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

     
      return {
        data: response.data,
        status: response.status
      }
    } catch (error: any) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/assignShcCluster\n` + 
        `\nresulted in error : \n`, error)

      const errorInfo = ErrorUtils.getApiErrorInfo(error);
      const errorResponseData = error?.response?.data || {};
      
      return {
        error: errorInfo,
        data: errorResponseData
      }
    }
  }

  // Generate web preview URL for user dashboard
  async generateUserWebDashboard(requestData: {
    uuid: string;
    destination_pilot_id?: string;
    api_host?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/user-web-dashboard`, requestData);
      return response
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/user-web-dashboard\n` +
        `\nresulted in error : \n`, error)
      return { error: ErrorUtils.getApiErrorInfo(error) }
    }
  }

  // Get application scope data based on user attributes with optional prefill support
  async getApplicationScope(queryParams: {
    project_id?: string;
  }) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/application-scope`, {
        params: queryParams,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/application-scope\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get DA Application Master data with filtering and pagination
  async getDAApplicationMaster(queryParams: any = {}) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/da-application-master`, {
        params: queryParams,
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/da-application-master\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Save application scope data
  async saveApplicationScope(scopeData: { project_id: string; applications: any[] }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/application-scope/save`, scopeData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/application-scope/save\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get DA Pipeline Master data with filtering and pagination
  async getDAPipelineMaster(queryParams: any = {}) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/pipeline-master`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/pipeline-master\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Get Pipeline Components data
  async getPipelineComponents(projectId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/pipeline`, {
        params: {
          projectId: projectId
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/pipeline\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Check whether recommendations have been cloned for a project
  async getRecoCloneStatus(projectId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/pipeline/reco-clone-status`, {
        params: {
          projectId: projectId
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/pipeline/reco-clone-status\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Call Hybrid Models API
  async callHybridModels(projectId: string, v1Fuels: string[], v2Fuels: string[]) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/hybrid-models`, {
        projectId: projectId,
        v1_fuels: v1Fuels,
        v2_fuels: v2Fuels
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/hybrid-models\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Fetch rate transition history for a user
  async fetchRateHistory(requestData: {
    uuid?: string;
    utility_name?: string;
    source_uuid?: string;
    source_env?: string;
    destination_pilot_id?: string;
    source_pilot_id?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      
      if (requestData.uuid) {
        queryParams.append('uuid', requestData.uuid);
      }
      if (requestData.utility_name) {
        queryParams.append('utility_name', requestData.utility_name);
      }
      if (requestData.source_uuid) {
        queryParams.append('source_uuid', requestData.source_uuid);
      }
      if (requestData.source_env) {
        queryParams.append('source_env', requestData.source_env);
      }
      if (requestData.destination_pilot_id) {
        queryParams.append('destination_pilot_id', requestData.destination_pilot_id);
      }
      if (requestData.source_pilot_id) {
        queryParams.append('source_pilot_id', requestData.source_pilot_id);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-rate-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetch-rate-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async archiveProject(projectId: string) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/archive-project`, {
        projectId: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/archive-project\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Call SHC API
  async callShc(projectId: string) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/shc`, {
        projectId: projectId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/shc\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // Enhanced Pipeline Process API - unified endpoint for all pipeline components
  async processPipeline(projectId: string, components: any, supportedLocale?: string | string[] | null) {
    try {
      const body: { projectId: string; components: any; supported_locale?: string | string[] } = {
        projectId: projectId,
        components: components
      };
      if (supportedLocale !== undefined && supportedLocale !== null) {
        body.supported_locale = Array.isArray(supportedLocale) ? supportedLocale : supportedLocale;
      }
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/pipeline/process`, body, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/pipeline/process\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchDataSchedulerHistory(requestData: {
    destination_pilot_id: string;
    job_name: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id,
        job_name: requestData.job_name,
        uuid: requestData.uuid
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/data-scheduler-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/data-scheduler-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }


  // Fetch data scheduler history for a specific user (all job types)
  async fetchUserDataSchedulerHistory(requestData: {
    destination_pilot_id: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id,
        uuid: requestData.uuid
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/data-scheduler-history?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/data-scheduler-history\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchConsumptionData(requestData: {
    uuid: string;
    destinationBaseUrl: string;
    meterFuel?: string;
    destinationPilotId?: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        uuid: requestData.uuid,
        destinationBaseUrl: requestData.destinationBaseUrl,
        meterFuel: requestData.meterFuel || ''
      });
      
      if (requestData.destinationPilotId) {
        queryParams.append('destinationPilotId', requestData.destinationPilotId);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/consumption-data?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/consumption-data\n` + 
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchUserAttribute(requestData: {
    uuid?: string;
    destination_pilot_id: string;
  }) {
    try {
      const queryParams = new URLSearchParams({
        destination_pilot_id: requestData.destination_pilot_id
      });

      if (requestData.uuid) {
        queryParams.append('uuid', requestData.uuid);
      }

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetch-user-attribute?${queryParams.toString()}`, {
        headers: {
          'Accept': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetch-user-attribute\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

    async assignPhysicalCluster(requestData: {
      uuid: string;
      destination_pilot_id: string;
    }) {
      try {
        const queryParams = new URLSearchParams({
          uuid: requestData.uuid,
          destination_pilot_id: requestData.destination_pilot_id
        });
  
        const response = await this.axiosClient.post(`${this.toolUrl}/v1/assign-physical-cluster?${queryParams.toString()}`, {}, {
          headers: {
            'Accept': 'application/json'
          }
        });
  
        return response;
      } catch (error) {
        this.rc.error(this.rc.getName(this),
          `Error >> POST request to : /v1/assign-physical-cluster\n` +
          `\nresulted in error : \n`, error);
        return { error: ErrorUtils.getApiErrorInfo(error) };
      }
    }

  async fetchBBUserProgram(requestData: {
    userUUID: string;
    utilityName: string;
    sourceEnvironment: string;
    destinationPilotId: string;
    utilityId: string;
    hasBBUserProgram: boolean;
  }) {
    try {
      const queryParams = new URLSearchParams({
        userUUID: requestData.userUUID,
        utilityName: requestData.utilityName,
        sourceEnvironment: requestData.sourceEnvironment,
        destinationPilotId: requestData.destinationPilotId,
        utilityId: requestData.utilityId,
        hasBBUserProgram: requestData.hasBBUserProgram.toString()
      });

      const response = await this.axiosClient.get(`${this.toolUrl}/v1/fetchProdUserData/BBUserProgram?${queryParams.toString()}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error: any) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/fetchProdUserData/BBUserProgram\n` +
        `\nresulted in error : \n`, error);
      
      // Extract errorDetails from response data if available (for 400 errors)
      if (error?.response?.data?.errorDetails) {
        return { error: error.response.data.errorDetails };
      } else if (error?.response?.data?.message) {
        return { error: error.response.data.message };
      }
      
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateSecondaryEmail(requestData: {
    uuid: string;
    destination_pilot_id: string;
    secondary_email: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-secondary-email`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-secondary-email\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getAttributes(projectId: string) {
    try {
      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/dcx-cluster/get-attributes/${projectId}`
      );

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/dcx-cluster/get-attributes/${projectId}\n` + 
        `\nresulted in error : \n`, error);

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      };
    }
  }

  async resetPilotCache(pilotId: string) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/reset-pilot-cache/${pilotId}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/reset-pilot-cache/${pilotId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createAttributes(requestData: {
    projectId: string;
    entityName: string;
    attributeName: string;
    attributeId: string;
    attributeValue: string;
    userAttributeEvaluatorType: string;
    description: string;
  }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/dcx-cluster/create-attributes`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/dcx-cluster/create-attributes\n` + 
        `\nresulted in error : \n`, error);

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      };
    }
  }
  async updateNameEmail(requestData: {
    uuid: string;
    destination_pilot_id: string;
    first_name: string;
    last_name?: string;
    primary_email: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-name-email`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-name-email\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateAddressZipcode(requestData: {
    uuid: string;
    destination_pilot_id: string;
    address: string;
    zipcode: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/update-address-zipcode`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error: any) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/update-address-zipcode\n` +
        `\nresulted in error : \n`, error);

      if (error?.response?.data?.message) {
        return {
          error: error.response.data.message
        }
      }

      return {
        error: ErrorUtils.getApiErrorInfo(error)
      }
    }
  }

  async rateTransition(requestData: {
    uuid: string;
    destination_pilot_id: string;
    rate_plan: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/rate-transition`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/rate-transition\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async syncWeatherData(requestData: {
    source_uuid: string[];
    source_environment: string;
    destinationEnvironment: string;
    destination_pilot_id: string;
    sourceCountry?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/fetchProdUserData/syncWeatherData`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/fetchProdUserData/syncWeatherData\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  } 

  async createCluster(requestData: {
    experimentId: string;
    projectId: string;
    clusterName: string;
    clusterType: string;
    clusterCategory: string[];
    clusterReference: string;
    uuids?: string[];
    evaluationCriteria?: string;
    control?: boolean;
    email?: boolean;
    paper?: boolean;
    sms?: boolean;
    userMovementAllowed?: boolean;
    userAssignmentAllowed?: boolean;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/create-cluster`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/create-cluster\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getClusterDetails(projectId: string, clusterId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/cluster-details/${projectId}/${clusterId}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> GET request to : /v1/cluster-details/${projectId}/${clusterId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getFeatureMetric(pilotId: string, level: string = 'LEVEL_2') {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/dcx-clusters/feature-metric/pilot/${pilotId}`, {
        headers: {
          'Content-Type': 'application/json'
        },
        params: {
          level: level
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dcx-clusters/feature-metric/pilot/${pilotId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async downloadFeatureMetric(pilotId: string, product?: string, level?: string) {
    try {
      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/dynamic-cx/feature-metric/pilot/${pilotId}/download`,
        {
          responseType: 'blob',
          params: {
            ...(product && { product }),
            // backend rejects level without product
            ...(product && level && { level })
          }
        }
      );

      const status = response.status ?? 0;
      if (status >= 200 && status < 300) {
        return {
          data: response.data,
          headers: response.headers,
          status: response.status,
          statusText: response.statusText
        };
      }
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dynamic-cx/feature-metric/pilot/${pilotId}/download\n` +
        `\nresulted in error : \n`, { status, statusText: response.statusText });
      return { error: ErrorUtils.getApiErrorInfo({ response }) };
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dynamic-cx/feature-metric/pilot/${pilotId}/download\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateCluster(clusterId: string, requestData: {
    cluster_categories: string[];
    cluster_name: string;
    evaluation_criteria: string;
    active: boolean;
  }) {
    try {
      const response = await this.axiosClient.put(`${this.toolUrl}/v1/dcx-clusters/${clusterId}`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> PUT request to : /v1/dcx-clusters/${clusterId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async compareDcxPromotionAttributes(projectId: string, currentEnv: string, targetEnv: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/dcx-promotion/attributes`, {
        headers: { 'Content-Type': 'application/json' },
        params: { project_id: projectId, current_env: currentEnv, target_env: targetEnv }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dcx-promotion/attributes\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async compareDcxPromotionClusters(projectId: string, currentEnv: string, targetEnv: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/dcx-promotion/clusters`, {
        headers: { 'Content-Type': 'application/json' },
        params: { project_id: projectId, current_env: currentEnv, target_env: targetEnv }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dcx-promotion/clusters\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async compareDcxPromotionSheets(projectId: string, currentEnv: string, targetEnv: string, level?: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/dcx-promotion/sheets`, {
        headers: { 'Content-Type': 'application/json' },
        params: { project_id: projectId, current_env: currentEnv, target_env: targetEnv, ...(level ? { level } : {}) }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dcx-promotion/sheets\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async stageDcxPromotionAttribute(requestData: {
    project_id: string;
    current_env: string;
    target_env: string;
    attributeId: string;
    attributeValue: string;
    sourceValue?: string;
    entityName?: string;
    attributeName?: string;
    userAttributeEvaluatorType?: string;
    description?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/attributes/stage`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/attributes/stage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async unstageDcxPromotionAttribute(targetEnv: string, projectId: string, attributeId: string) {
    try {
      const response = await this.axiosClient.delete(`${this.toolUrl}/v1/dcx-promotion/attributes/stage`, {
        headers: { 'Content-Type': 'application/json' },
        params: { target_env: targetEnv, project_id: projectId, attributeId }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> DELETE request to : /v1/dcx-promotion/attributes/stage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async stageDcxPromotionCluster(requestData: {
    project_id: string;
    current_env: string;
    target_env: string;
    clusterReference: string;
    evaluationCriteria: string;
    sourceValue?: string;
    experimentId?: string;
    clusterName?: string;
    clusterType?: string;
    control?: boolean;
    email?: boolean;
    paper?: boolean;
    sms?: boolean;
    userMovementAllowed?: boolean;
    userAssignmentAllowed?: boolean;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/clusters/stage`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/clusters/stage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async unstageDcxPromotionCluster(targetEnv: string, projectId: string, clusterReference: string) {
    try {
      const response = await this.axiosClient.delete(`${this.toolUrl}/v1/dcx-promotion/clusters/stage`, {
        headers: { 'Content-Type': 'application/json' },
        params: { target_env: targetEnv, project_id: projectId, clusterReference }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> DELETE request to : /v1/dcx-promotion/clusters/stage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** Bulk unstage — attributes, clusters and/or sheet rows for one environment+pilot. POST
   *  rather than DELETE because the selections are arrays. */
  async unstageDcxPromotionSelection(requestData: {
    env_id: string | number;
    project_id: string;
    types?: string[];
    attributeIds?: string[];
    clusterReferences?: string[];
    sheets?: { events?: string[]; level?: string };
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/unstage`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/unstage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateDcxPromotionAttribute(requestData: {
    project_id: string;
    target_env: string;
    entityName: string;
    attributeName: string;
    attributeId: string;
    attributeValue: string;
    userAttributeEvaluatorType: string;
    description: string;
  }) {
    try {
      const response = await this.axiosClient.put(`${this.toolUrl}/v1/dcx-promotion/attributes/update`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> PUT request to : /v1/dcx-promotion/attributes/update\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateDcxPromotionCluster(requestData: {
    project_id: string;
    target_env: string;
    clusterId: string;
    evaluationCriteria: string;
    clusterReference?: string;
  }) {
    try {
      const response = await this.axiosClient.put(`${this.toolUrl}/v1/dcx-promotion/clusters/update`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> PUT request to : /v1/dcx-promotion/clusters/update\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createDcxPromotionAttribute(requestData: {
    project_id: string;
    target_env: string;
    entityName: string;
    attributeName: string;
    attributeId: string;
    attributeValue: string;
    userAttributeEvaluatorType: string;
    description: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/attributes/create`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/attributes/create\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Import Access Matrix — POST /v1/dcx-promotion/sheets/import copies the full LEVEL_1 +
   * LEVEL_2 feature access matrix from source_env onto target_env, replacing it. The target's
   * type decides whether it is staged (nonprod/prod) or written through.
   */
  async importDcxPromotionSheets(requestData: {
    project_id: string;
    source_env: string | number;
    target_env: string | number;
    level: 'LEVEL_1' | 'LEVEL_2';
    events?: string[];
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/sheets/import`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/sheets/import\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * GET /v1/dcx-promotion/sheets/staged — read back what's staged for a target env (nonprod/
   * prod), so the FE can mark already-staged cells purple in the editing grids.
   */
  async getDcxPromotionStagedSheets(params: { target_env: string | number; project_id: string; level?: string }) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/dcx-promotion/sheets/staged`, {
        headers: { 'Content-Type': 'application/json' },
        params
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/dcx-promotion/sheets/staged\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * PUT /v1/dcx-promotion/sheets/stage — edit staged sheet rows in bulk (nonprod/prod target),
   * all in one transaction. Update-only: a row not already staged by a prior
   * POST .../sheets/import comes back under `notStaged` instead of being created.
   */
  async stageDcxPromotionSheetRows(requestData: {
    target_env: string | number;
    project_id: string;
    rows: { featureId: string; clusterReference?: string; resolution: string; position?: number | null; mandatory?: boolean }[];
  }) {
    try {
      const response = await this.axiosClient.put(`${this.toolUrl}/v1/dcx-promotion/sheets/stage`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> PUT request to : /v1/dcx-promotion/sheets/stage\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Build the shell bundle (zip of runnable curl scripts) for the selected diff rows.
   * POST /v1/dcx-promotion/create-bundle — returns a binary zip; `target_env` resolves through
   * the same dual-target rule as the compares, so a nonprod target also gets its prod pair,
   * each in its own directory.
   */
  async createDcxPromotionBundle(requestData: {
    project_id: string;
    target_env: string | number;
    attributes?: any[];
    clusters?: any[];
    sheets?: any[];
  }) {
    try {
      const resp = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/create-bundle`, requestData, {
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob'
      });
      return { data: resp.data, headers: resp.headers, status: resp.status, statusText: resp.statusText };
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/create-bundle\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createDcxPromotionCluster(requestData: {
    project_id: string;
    target_env: string;
    /** Omitted by default — the BFF resolves the target env's experiment from clusterCategory. */
    experimentId?: string;
    clusterCategory?: string;
    clusterReference: string;
    evaluationCriteria: string;
    clusterName?: string;
    clusterType?: string;
    control?: boolean;
    email?: boolean;
    paper?: boolean;
    sms?: boolean;
    userMovementAllowed?: boolean;
    userAssignmentAllowed?: boolean;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/clusters/create`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/clusters/create\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async syncDcxPromotionClustersDb(requestData: { project_id: string; environment_id: string }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-promotion/clusters/sync-db`, requestData, {
        headers: { 'Content-Type': 'application/json' }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/dcx-promotion/clusters/sync-db\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async saveFeatureAccess(requestData: {
    projectId: string;
    files: {
      component: 'EMAIL' | 'DASHBOARD' | 'HER';
      level: 'L1' | 'L2';
      headers: string[];
      features: {
        featureId: string;
        values: string[];
      }[];
    }[];
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/feature-resolution`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/feature-resolution\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

    async getColorPalette(params: { channel: 'DIGITAL' | 'PAPER'; backgroundHEX: string; projectId?: string }) {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append('channel', params.channel);
        queryParams.append('backgroundHEX', params.backgroundHEX);
        if (params.projectId) {
          queryParams.append('projectId', params.projectId);
        }
  
        const resp = await this.axiosClient.get(
          `${this.toolUrl}/v1/cx/color-palette?${queryParams.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        return resp
      } catch (error) {
        this.rc.error(this.rc.getName(this),
          `Error >> GET request to : /v1/cx/color-palette\n` +
          `\nresulted in error : \n`, error)
        return { error: ErrorUtils.getApiErrorInfo(error) }
      }
    }
  

    async getColorImpact(params: { id: string; shade: string; projectId?: string; channel: 'DIGITAL' | 'PAPER'; level: 'PILOT' | 'CLUSTER' }) {
      try {
        const queryParams = new URLSearchParams();
        if (params.projectId) {
          queryParams.append('projectId', params.projectId);
        }
        queryParams.append('channel', params.channel);
        queryParams.append('level', params.level);
  
        const resp = await this.axiosClient.get(
          `${this.toolUrl}/v1/cx/color-palette/${params.id}/${params.shade}/impact?${queryParams.toString()}`,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        return resp
      } catch (error) {
        this.rc.error(this.rc.getName(this),
          `Error >> GET request to : /v1/cx/color-palette/${params.id}/${params.shade}/impact\n` +
          `\nresulted in error : \n`, error)
        return { error: ErrorUtils.getApiErrorInfo(error) }
      }
    }
  

    async saveColorPalette(params: {
      project_id: string;
      channel: 'DIGITAL' | 'PAPER';
      id: string;
      shade: string;
      newHexValue: string;
      config_level: string;
      source_name: string;
    }) {
      try {
        const resp = await this.axiosClient.post(
          `${this.toolUrl}/v1/cx/color-palette/save`,
          params,
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        )
        return resp
      } catch (error) {
        this.rc.error(this.rc.getName(this),
          `Error >> POST request to : /v1/cx/color-palette/save\n` +
          `\nresulted in error : \n`, error)
        return { error: ErrorUtils.getApiErrorInfo(error) }
      }
    }

  async getFeatureMetricSchema(projectId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/feature-metric/schema`, {
        params: {
          project_id: projectId
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });

          return response;
        } catch (error) {
          this.rc.error(this.rc.getName(this), 
            `Error >> GET request to : /v1/feature-metric/schema\n` +
            `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getDataScenarios(params: { templateName: string; pilotId: string }) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/cx/data-scenarios`, {
        params: {
          templateName: params.templateName,
          pilotId: params.pilotId,
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /api/data-scenarios\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Push new sections for email templates
   * POST /v1/dcx-clusters/push-new-sections
   */
  async pushNewSections(payload: {
    project_id: string;
    email: { event: string; sections: string[] }[];
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/dcx-clusters/push-new-sections`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this), 
        `Error >> POST request to : /v1/dcx-clusters/push-new-sections\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }
  
  async createDataScenarioWithPayloads(requestData: {
    pilotId: string;
    scenarioId: string;
    scenarioDisplayName: string;
    productType: string;
    scenarioLocale: string;
    fuelType: string;
    templateName: string;
    elementNames: string[];
    defaultScenario: boolean;
    userClusters: string[];
    scenarioDocumentation?: string;
    payloads: Array<{ documentId?: string; templateTypeName: string; payload: Record<string, unknown> }>;
    documentId?: string;
  }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/cx/create-with-payloads`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/cx/create-with-payloads\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getDataScenarioDetails(params: { pilotId: string; scenarioId: string }) {
    try {
      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/cx/pilot/${params.pilotId}/scenario/${params.scenarioId}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /api/data-scenario/v1/pilot/${params.pilotId}/scenario/${params.scenarioId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async deleteDataScenario(params: { pilotId: string; scenarioId: string }) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/v1/cx/pilot/${params.pilotId}/scenario/${params.scenarioId}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> DELETE request to : /api/data-scenario/v1/pilot/${params.pilotId}/scenario/${params.scenarioId}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getEmailStatus(requestData: {
    project_id: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('project_id', requestData.project_id);
      queryParams.append('uuid', requestData.uuid);

      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/self-serve-email/status?${queryParams.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/self-serve-email/status\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async resetEmailSession(requestData: {
    uuid: string;
    projectId: string;
  }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/self-serve-email/reset-session`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/self-serve-email/reset-session\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async triggerEmailRuns(requestData: {
    uuid: string;
    projectId: string;
    emailEvents: string[];
    allEmailEvents: string[];
    herReports: string[];
    [key: string]: any;
  }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/self-serve-email/runs`,
        requestData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/self-serve-email/runs\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getUserEmails(requestData: {
    project_id: string;
    uuid: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('project_id', requestData.project_id);
      queryParams.append('uuid', requestData.uuid);

      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/self-serve/get-user-emails?${queryParams.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/self-serve/get-user-emails\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getIngestedUsers(requestData: {
    projectId: string;
    /** 1-based page number (default 1) */
    page?: number;
    /** Page size (default 10) */
    limit?: number;
    /** Zero-based offset (alternative to page) */
    offset?: number;
    /** Free-text search (e.g. email) */
    text?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('projectId', requestData.projectId);
      const limit = Math.max(1, requestData.limit ?? 10);
      queryParams.append('limit', String(limit));

      if (requestData.offset != null) {
        queryParams.append('offset', String(Math.max(0, requestData.offset)));
      } else {
        const page = requestData.page ?? 1;
        queryParams.append('page', String(page));
      }

      const text = requestData.text?.trim();
      if (text) {
        queryParams.append('text', text);
      }

      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/ingested-users?${queryParams.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /v1/ingested-users\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async fetchValidMeterFuel(requestData: {
    destination_pilot_id: string;
    source_uuids: string[];
  }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/valid-meter-fuel/${requestData.destination_pilot_id}`,
        {
          source_uuids: requestData.source_uuids
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /v1/valid-meter-fuel/${requestData.destination_pilot_id}\n` +
        `\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getUnresolvedConfigValue(params: {
    pilot_id: string;
    config_key: string;
    config_type: string;
    env_id?: string | number;
    environment?: string;
    api_host?: string;
  }) {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('config_key', params.config_key);
      queryParams.append('config_type', params.config_type);
      if (params.env_id != null && String(params.env_id) !== '') {
        queryParams.append('env_id', String(params.env_id));
      }
      if (params.environment) {
        queryParams.append('environment', params.environment);
      }
      if (params.api_host) {
        queryParams.append('api_host', params.api_host);
      }

      const queryString = queryParams.toString();
      const url = `${this.toolUrl}/v1/media/unresolved-config/${encodeURIComponent(params.pilot_id)}${
        queryString ? `?${queryString}` : ''
      }`;

      const response = await this.axiosClient.get(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/media/unresolved-config/${params.pilot_id}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getMediaImages(params: { project_id: string; environment?: string; api_host?: string }) {
    try {
      const queryParams = new URLSearchParams();
      if (params.environment) {
        queryParams.append('environment', params.environment);
      }
      if (params.api_host) {
        queryParams.append('api_host', params.api_host);
      }

      const queryString = queryParams.toString();
      const url = `${this.toolUrl}/v1/media/get-images/${encodeURIComponent(params.project_id)}${
        queryString ? `?${queryString}` : ''
      }`;

      const response = await this.axiosClient.get(url, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/media/get-images/${params.project_id}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async uploadMedia(params: {
    pilotId: string;
    configKey: string;
    configType: string;
    file: File;
    environment?: 'uat' | 'nonprod' | 'prod';
    imageDimension?: string;
    uploadOnly?: boolean;
  }) {
    try {
      const formData = new FormData();
      formData.append('pilotId', params.pilotId);
      formData.append('configKey', params.configKey);
      formData.append('configType', params.configType);
      if (params.environment) {
        formData.append('environment', params.environment);
      }
      formData.append('file', params.file);
      if (params.imageDimension) {
        formData.append('imageDimension', params.imageDimension);
      }

      const url = params.uploadOnly
        ? `${this.toolUrl}/v1/media/upload?uploadOnly=true`
        : `${this.toolUrl}/v1/media/upload`;
      const response = await this.axiosClient.post(url, formData);
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/media/upload\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async revertMediaConfig(params: { projectId: string; configKey: string; configType: string }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/media/revert-config`, params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/media/revert-config\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async promoteMediaConfig(params: {
    pilotId: string;
    configType: string;
    configKey: string;
    configValue: string;
    envId: number;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/media/promote-config`, params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/media/promote-config\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getMediaEnvMatrix(
    pilotId: string,
    params: {
      current_env_id: number;
      target_env_ids: number[];
      target_configs: Array<{ configType: string; configKey: string }> | Record<string, string>;
    },
  ) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/v1/media/env-matrix/${encodeURIComponent(pilotId)}`,
        params,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/media/env-matrix/${pilotId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createBrandGuideline(params: {
    config_type: string;
    config_key: string;
    category: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/v1/media/brand-guidelines`, params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /v1/media/brand-guidelines\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getBrandGuidelines() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/v1/media/brand-guidelines`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/media/brand-guidelines\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getBrandGuidelineById(id: string) {
    try {
      const response = await this.axiosClient.get(
        `${this.toolUrl}/v1/media/brand-guidelines/${encodeURIComponent(id)}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /v1/media/brand-guidelines/${id}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateBrandGuideline(
    id: string,
    params: {
      config_type: string;
      config_key: string;
      category: string;
    },
  ) {
    try {
      const response = await this.axiosClient.put(
        `${this.toolUrl}/v1/media/brand-guidelines/${encodeURIComponent(id)}`,
        params,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PUT request to : /v1/media/brand-guidelines/${id}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // RBAC — Feature Groups
  async getFeatureGroups() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/feature-groups`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/feature-groups\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getRbacFeatures() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/features`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/features\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createRbacFeature(body: {
    endpoint: string;
    path: string;
    methods?: string;
    domain?: string;
    label?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/rbac/features`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/features\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateRbacFeature(
    featureId: number | string,
    body: { label?: string; domain?: string },
  ) {
    try {
      const response = await this.axiosClient.patch(`${this.toolUrl}/rbac/features/${featureId}`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PATCH request to : /rbac/features/${featureId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async addFeaturesToGroup(groupId: number | string, featureIds: Array<number | string>) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/feature-groups/${groupId}/features`,
        { feature_ids: featureIds },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/feature-groups/${groupId}/features\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async removeFeatureFromGroup(groupId: number | string, featureId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/feature-groups/${groupId}/features/${featureId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/feature-groups/${groupId}/features/${featureId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateFeatureGroup(
    groupId: number | string,
    body: { name?: string; description?: string; parent_group_id?: number | string | null; sort_order?: number },
  ) {
    try {
      const response = await this.axiosClient.patch(`${this.toolUrl}/rbac/feature-groups/${groupId}`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PATCH request to : /rbac/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createFeatureGroup(body: {
    key: string;
    name: string;
    description?: string;
    parent_group_id?: number | string | null;
    sort_order?: number;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/rbac/feature-groups`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/feature-groups\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async deleteFeatureGroup(groupId: number | string, strategy?: 'block' | 'reparent') {
    try {
      const url = strategy
        ? `${this.toolUrl}/rbac/feature-groups/${groupId}?strategy=${strategy}`
        : `${this.toolUrl}/rbac/feature-groups/${groupId}`;
      const response = await this.axiosClient.delete(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // RBAC — Roles
  async getRoles() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/roles`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/roles\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getRolePermissionTree(roleId: number | string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/roles/${roleId}/permissions/tree`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/roles/${roleId}/permissions/tree\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async grantRoleFeatureGroup(roleId: number | string, groupId: number | string) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/roles/${roleId}/feature-groups/${groupId}`,
        {},
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/roles/${roleId}/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async revokeRoleFeatureGroup(roleId: number | string, groupId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/roles/${roleId}/feature-groups/${groupId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/roles/${roleId}/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async attachRoleFeatures(roleId: number | string, featureIds: Array<number | string>) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/roles/${roleId}/features`,
        { feature_ids: featureIds },
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/roles/${roleId}/features\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async detachRoleFeatures(roleId: number | string, featureIds: Array<number | string>) {
    try {
      const response = await this.axiosClient.delete(`${this.toolUrl}/rbac/roles/${roleId}/features`, {
        headers: { 'Content-Type': 'application/json' },
        data: { feature_ids: featureIds },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/roles/${roleId}/features\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // RBAC — Users
  async getRbacUsers() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/users`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/users\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getUserPermissionTree(userId: number | string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/users/${userId}/permissions/tree`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/users/${userId}/permissions/tree\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async grantUserFeatureGroup(userId: number | string, groupId: number | string) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/users/${userId}/feature-groups/${groupId}`,
        {},
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/users/${userId}/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async revokeUserFeatureGroup(userId: number | string, groupId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/users/${userId}/feature-groups/${groupId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/users/${userId}/feature-groups/${groupId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async grantUserFeature(userId: number | string, featureId: number | string) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/users/${userId}/features/${featureId}`,
        {},
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/users/${userId}/features/${featureId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async revokeUserFeature(userId: number | string, featureId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/users/${userId}/features/${featureId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/users/${userId}/features/${featureId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // RBAC — Model (record-level) restrictions
  async getEnforcedModels() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/model-restrictions/enforced-models`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/model-restrictions/enforced-models\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getModelAttributes(model: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/model-restrictions/${model}/attributes`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/model-restrictions/${model}/attributes\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getModelAttributeValues(model: string, attribute: string, params: { q?: string; page?: number; per_page?: number }) {
    try {
      const response = await this.axiosClient.get(
        `${this.toolUrl}/rbac/model-restrictions/${model}/attributes/${attribute}/values`,
        { headers: { 'Content-Type': 'application/json' }, params },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/model-restrictions/${model}/attributes/${attribute}/values\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getRoleModelRestrictions(roleId: number | string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/roles/${roleId}/model-restrictions`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/roles/${roleId}/model-restrictions\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getUserModelRestrictions(userId: number | string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/rbac/users/${userId}/model-restrictions`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> GET request to : /rbac/users/${userId}/model-restrictions\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createRoleModelRestriction(roleId: number | string, body: { model: string; attribute: string; values: string[] }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/roles/${roleId}/model-restrictions`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/roles/${roleId}/model-restrictions\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateRoleModelRestriction(roleId: number | string, restrictionId: number | string, body: { attribute?: string; values?: string[] }) {
    try {
      const response = await this.axiosClient.put(
        `${this.toolUrl}/rbac/roles/${roleId}/model-restrictions/${restrictionId}`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PUT request to : /rbac/roles/${roleId}/model-restrictions/${restrictionId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async deleteRoleModelRestriction(roleId: number | string, restrictionId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/roles/${roleId}/model-restrictions/${restrictionId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/roles/${roleId}/model-restrictions/${restrictionId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async createUserModelRestriction(userId: number | string, body: { model: string; attribute: string; values: string[] }) {
    try {
      const response = await this.axiosClient.post(
        `${this.toolUrl}/rbac/users/${userId}/model-restrictions`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> POST request to : /rbac/users/${userId}/model-restrictions\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async updateUserModelRestriction(userId: number | string, restrictionId: number | string, body: { attribute?: string; values?: string[] }) {
    try {
      const response = await this.axiosClient.put(
        `${this.toolUrl}/rbac/users/${userId}/model-restrictions/${restrictionId}`,
        body,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> PUT request to : /rbac/users/${userId}/model-restrictions/${restrictionId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async deleteUserModelRestriction(userId: number | string, restrictionId: number | string) {
    try {
      const response = await this.axiosClient.delete(
        `${this.toolUrl}/rbac/users/${userId}/model-restrictions/${restrictionId}`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      return response;
    } catch (error) {
      this.rc.error(
        this.rc.getName(this),
        `Error >> DELETE request to : /rbac/users/${userId}/model-restrictions/${restrictionId}\n` + `\nresulted in error : \n`,
        error,
      );
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  // ===========================================================================
  // NBI INSIGHTS (epic DA-697 / II-FE-1)
  //
  // proxypulse relays quill's nine insight routes (DA-2385); quill-fe never
  // calls quill directly. RBAC and audit are enforced there, and proxypulse
  // forwards the end-user JWT on every call — which the request interceptor
  // above already does via `session.getCMSAccessToken()`.
  //
  // Payload shapes: `docs/nbi-insights-api-contract.md` in bidgely-quill, which
  // is authoritative. Prefer `features/interesting-insight/api/insightAPI.ts`
  // over calling these directly — it normalises the envelope and keeps the
  // per-item error array intact.
  // ===========================================================================

  async getInsightCatalog() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/${BFF_INSIGHT_BASE}/catalog`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /${BFF_INSIGHT_BASE}/catalog\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Whole-pilot list, partitioned { draft, modified, published }.
   * No paging/sort/search params exist — the caller pages locally.
   */
  async getInsights(utilityDocumentId: string) {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/${BFF_INSIGHT_BASE}`, {
        params: { utilityDocumentId },
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /${BFF_INSIGHT_BASE}\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async getInsightReviewWorkflow() {
    try {
      const response = await this.axiosClient.get(`${this.toolUrl}/${BFF_INSIGHT_BASE}/review-workflow`, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> GET request to : /${BFF_INSIGHT_BASE}/review-workflow\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** quill derives `code` and `insightId`; a client-supplied value is rejected. */
  async createInsight(body: { utilityDocumentId: string; locale?: string; codeOverride?: string | null; data: Record<string, any> }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Batch diff-then-patch. `data` rows carry ONLY changed fields, and must
   * omit `code`, `insightId` and `editStatus` — all server-owned.
   */
  async updateInsights(body: { pilotId: string; data: Array<Record<string, any>> }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/update`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/update\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** Publishing resets `verificationStatus` to TODO — refetch, do not assume. */
  async publishInsights(body: { pilotId: string; data: string[] }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/publish`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/publish\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  async unpublishInsights(body: { pilotId: string; data: string[] }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/unpublish`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/unpublish\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** Reverts to the published version, or DELETES a never-published draft. */
  async discardInsightDrafts(body: { pilotId: string; data: string[] }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/discard`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/discard\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * Stateless token/registry check, relayed from quill (contract §2b).
   * Returns quill's `{ data: { valid, errors, placeholders } }` envelope.
   */
  async validateInsightTemplateText(body: { templateString: string; type?: string | null }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/validate-template-text`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/validate-template-text\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /**
   * AI Writing Assistant (DA-2401). Every candidate is token-validated in
   * proxypulse before it comes back; invalid ones arrive FLAGGED, not dropped.
   * `count` is clamped server-side to 1..3.
   */
  async generateInsightAlternative(body: {
    templateString: string;
    tone?: string;
    readingLevel?: string;
    additionalInstructions?: string;
    type?: string | null;
    count?: number;
    pilotId?: string;
    insightDocumentId?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/generate-alternative`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/generate-alternative\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** Advisory meaning-vs-classification check (DA-2401). Never gates a save. */
  async semanticValidateInsight(body: {
    templateString: string;
    classification?: Record<string, unknown>;
    pilotId?: string;
    insightDocumentId?: string;
  }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/semantic-validate`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/semantic-validate\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

  /** Response carries a FRESH `allowedTransitions` — re-render the dropdown from it. */
  async tagInsightRecommendations(body: { insightDocumentId: string; recommendationDocumentIds: string[]; mode?: 'SET' | 'ADD' | 'REMOVE' }) {
    try {
      const response = await this.axiosClient.post(`${this.toolUrl}/${BFF_INSIGHT_BASE}/tag-recommendations`, body, {
        headers: { 'Content-Type': 'application/json' },
      });
      return response;
    } catch (error) {
      this.rc.error(this.rc.getName(this),
        `Error >> POST request to : /${BFF_INSIGHT_BASE}/tag-recommendations\n\nresulted in error : \n`, error);
      return { error: ErrorUtils.getApiErrorInfo(error) };
    }
  }

}